import { useCallback, useEffect, useRef, useState } from 'react';

import { useTracker } from '@archipro-website/tracker';
import { TwoPaneModal } from '@rocco/components/two-pane-modal';
import { useUserLoggedIn } from '@modules/user';
import { useFeature } from '~/modules/root';
import { AuthModalBrandPane } from '~/modules-rocco/user/component/auth-modal/AuthModalBrandPane';
import { getAuthBrandStatement } from '~/modules-rocco/user/component/auth-modal/auth-intent-copy';
import { isReturningPasskeyDevice } from '~/modules/user/passkey/passkey-device-match';
import { useAuthGateSegment } from '~/modules-rocco/user/auth-gate/use-auth-gate-segment';
import { trackAuthGateActionResumed } from '~/modules-rocco/user/auth-gate/auth-gate-events';
import { useGateSubjectValue } from '~/modules-rocco/user/auth-gate/gate-subject';
import {
    resolveGatePaneImage,
    resolveGateThumbnail,
} from '~/modules-rocco/user/auth-gate/gate-pane-image';
import {
    grantSpecsAccess,
    requestDownloadAccessMutation,
} from '~/modules/product/hook/use-gated-downloads';
import { useDownloadGate } from '../download-gate-state';
import { DownloadGateSuccess } from './DownloadGateSuccess';

/** Names this gate on every funnel event as well as selecting its intent copy. */
const DOWNLOAD_GATE_AUTH_SOURCE = 'productFiles' as const;

export interface DownloadGateModalProps {
    /** Overrides how the file opens. Defaults to a new tab. */
    onDownload?: (link: string) => void;
    /** Overrides the request-access call. Defaults to the shared mutation. */
    onRequestAccess?: (intent: {
        productID: number;
        professionalID: number;
    }) => void;
    /**
     * Overrides recording the Specs-tier lead that a completed download earns.
     * Defaults to the same grant the logged-in path makes.
     */
    onGrantSpecsAccess?: (intent: {
        productID: number;
        professionalID: number;
    }) => void;
}

/**
 * Download gating in the revamped two-pane gate: [download attempt] → [auth] →
 * [file ready]. Form-less, so the attempt itself is the pre-gate step — the
 * reader has already chosen their file and the gate sits between choosing it
 * and getting it.
 *
 * The download runs from the success screen rather than on arrival. Browsers
 * only permit `window.open` inside a user gesture and the auth round-trip
 * consumes the original, so an automatic resume would be swallowed by the popup
 * blocker on exactly the journeys this gate exists to serve.
 */
export const DownloadGateModal = (props: DownloadGateModalProps = {}) => {
    // The gate's atoms are sessionStorage-backed, so an intent left over from a
    // flag-on session would otherwise render this gate — and hand out a "file
    // is ready" screen — on a build meant to have no gate at all. The other
    // three gates are all mounted behind the flag; this one was not.
    const enabled = useFeature('auth_modal_2026');
    if (!enabled) return null;

    return <DownloadGateModalInner {...props} />;
};

const DownloadGateModalInner = ({
    onDownload = (link) => window.open(link, '_blank', 'noopener'),
    onRequestAccess = ({ productID, professionalID }) => {
        void requestDownloadAccessMutation(professionalID, productID).catch(
            () => undefined,
        );
    },
    onGrantSpecsAccess = ({ productID, professionalID }) =>
        grantSpecsAccess(professionalID, productID),
}: DownloadGateModalProps = {}) => {
    const { intent, isOpen, close } = useDownloadGate();
    const { isLoggedIn } = useUserLoggedIn();
    const tracker = useTracker();
    const subject = useGateSubjectValue();
    /** The segment has been entered for this opening of the modal. */
    const enteredRef = useRef(false);
    /** Last intent shown, so the sheet can animate out after it is cleared. */
    const lastIntentRef = useRef<typeof intent>(null);

    /**
     * `auth` until the segment says otherwise.
     *
     * Deriving the screen from `!gate.isActive` made 'not entered yet' and
     * 'resolved' the same state, and the segment is entered from an effect —
     * so a guest opening the gate saw the 'your file is ready' screen, live
     * Download button and all, for the frame before the auth panel replaced it.
     */
    const [step, setStep] = useState<'auth' | 'ready'>('auth');

    // Written from an effect, not during render: a render-phase ref write can
    // be committed by a pass React later discards, and this ref decides what
    // the sheet shows on its way out.
    useEffect(() => {
        if (intent) lastIntentRef.current = intent;
    }, [intent]);

    const gate = useAuthGateSegment({
        authSource: DOWNLOAD_GATE_AUTH_SOURCE,
        onResolved: () => setStep('ready'),
    });

    // A reader who arrives already authenticated — social round-trip, or an
    // account made for them — has passed the wall; the ask still belongs here,
    // so the segment decides whether to show it or resolve straight through.
    useEffect(() => {
        if (!isOpen) {
            gate.reset();
            enteredRef.current = false;
            setStep('auth');
            return;
        }
        // Placing the reader is a once-per-open decision. Signing in inside the
        // modal flips `isLoggedIn`, and without this the segment would be
        // entered a second time, dismissing a Secure Sign-In ask already on
        // screen and resolving the gate twice.
        if (enteredRef.current) return;
        enteredRef.current = true;
        if (isLoggedIn) {
            gate.enterAuthenticated();
        } else {
            gate.open();
        }
        // The segment's identity changes as it advances, so it stays out of the
        // dependencies; this runs on the modal opening.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isLoggedIn]);

    const handleClose = useCallback(() => {
        gate.close();
        close();
    }, [gate, close]);

    const handleDownload = useCallback(() => {
        if (!intent) return;
        if (intent.action === 'requestAccess') {
            onRequestAccess({
                productID: intent.productID,
                professionalID: intent.professionalID,
            });
        } else if (intent.link) {
            onDownload(intent.link);
            // A Specs-tier file is free to take but not free to the
            // professional: taking it is the lead. The logged-in path records
            // it as the file opens, and the gate owes the same — without this
            // every download that went through auth reached the reader without
            // ever reaching the client who published it.
            if (intent.isSpecsTier) {
                onGrantSpecsAccess({
                    productID: intent.productID,
                    professionalID: intent.professionalID,
                });
            }
        }
        // This gate cannot auto-resume — browsers only allow `window.open`
        // inside a user gesture, and the auth round trip consumed the original.
        // Taking the explicit button IS the resume, so a reader who authenticated
        // and never pressed it is drop-off this event makes visible.
        trackAuthGateActionResumed({
            tracker,
            authSource: DOWNLOAD_GATE_AUTH_SOURCE,
            landed: true,
            data: { action: intent.action },
        });
        close();
    }, [
        intent,
        onDownload,
        onRequestAccess,
        onGrantSpecsAccess,
        close,
        tracker,
    ]);

    const handleExplore = useCallback(() => {
        close();
        window.location.href = '/post-sign-up';
    }, [close]);

    /**
     * The intent that was on screen, kept for as long as the sheet is closing.
     *
     * `close()` clears `isOpen` and the intent together — deliberately, so a
     * dismissed gate cannot leave an intent behind to re-render itself on a
     * later visit. But rendering straight off `intent` meant the modal
     * unmounted the instant it was told to close, rather than animating out on
     * `open={false}`: vaul's `onOpenChange(false)` never fired, so it never
     * restored the body scroll position or `pointer-events`, and its unmount
     * fallback bails whenever another drawer is in the DOM — which on iOS left
     * the page unscrollable until a reload. Holding the last one here keeps the
     * component mounted through the exit without keeping anything in storage.
     */

    const displayIntent = intent ?? lastIntentRef.current;

    if (!displayIntent) return null;

    const paneImage = resolveGatePaneImage({
        override: displayIntent.productImage,
        subject,
        gate: 'download',
    });

    return (
        <TwoPaneModal
            corners="rounded"
            open={isOpen}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) handleClose();
            }}
            title={
                step === 'ready'
                    ? 'Your file is ready to download'
                    : 'Sign in to download'
            }
            // The item being downloaded, not stock brand imagery — the reader
            // should see the product whose file they asked for.
            pane={
                <AuthModalBrandPane
                    statement={getAuthBrandStatement(
                        'productFiles',
                        isReturningPasskeyDevice(),
                    )}
                    image={paneImage}
                />
            }
        >
            {step !== 'ready' ? (
                gate.element
            ) : (
                <DownloadGateSuccess
                    fileName={displayIntent.fileName}
                    fileImage={resolveGateThumbnail({
                        override: displayIntent.productImage,
                        subject,
                    })}
                    onDownload={handleDownload}
                    onExplore={handleExplore}
                />
            )}
        </TwoPaneModal>
    );
};
